/**
 * OrgSwitcher — dropdown that lists the user's organizations and lets
 * them switch the active org.
 *
 * Visual: the trigger shows the current org's icon (emoji or first
 * letter) + display_name, with a small chevron. Clicking opens a
 * dropdown with every org the user belongs to (or every active org for
 * system founders).
 *
 * Inspired by the workspace label in the existing left rail; matches
 * the monochrome rules (ADR-057): no coloured chrome, hover-only tint.
 */
import { Building2, Check, ChevronsUpDown, Plus } from 'lucide-react';

import { useActiveOrg } from '../../lib/active-org';
import { cn } from '../../lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

interface OrgSwitcherProps {
  className?: string;
  /** Called when the user picks "Create org" from the dropdown menu. */
  onCreate?: () => void;
}

function OrgIcon({ icon, displayName }: { icon: string | null; displayName: string }) {
  if (icon && icon.length <= 4) {
    return <span className="text-base leading-none">{icon}</span>;
  }
  // Fallback — a single uppercase initial in a low-contrast pill.
  const initial = displayName.trim().charAt(0).toUpperCase() || '?';
  return (
    <span className="flex h-5 w-5 items-center justify-center rounded bg-foreground/10 text-[10px] font-semibold uppercase text-foreground">
      {initial}
    </span>
  );
}

export function OrgSwitcher({ className, onCreate }: OrgSwitcherProps) {
  const { orgs, activeOrg, activeOrgId, setActiveOrg, isLoading } = useActiveOrg();

  if (isLoading && orgs.length === 0) {
    return (
      <div
        className={cn(
          'inline-flex items-center gap-2 rounded-md border border-border bg-background px-3 py-1.5 text-sm text-muted-foreground',
          className,
        )}
      >
        <Building2 className="h-3.5 w-3.5" aria-hidden />
        <span className="text-xs">Loading Workspaces…</span>
      </div>
    );
  }

  if (orgs.length === 0) {
    return (
      <button
        type="button"
        onClick={onCreate}
        className={cn(
          'inline-flex items-center gap-2 rounded-md border border-dashed border-border bg-background px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:border-foreground/40 hover:text-foreground',
          className,
        )}
      >
        <Plus className="h-3.5 w-3.5" aria-hidden />
        <span>Create Workspace</span>
      </button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Switch Workspace"
          className={cn(
            'group inline-flex max-w-[260px] items-center gap-2 rounded-md border border-border bg-background px-2.5 py-1.5 text-sm transition-colors hover:bg-foreground/[0.04]',
            className,
          )}
        >
          {activeOrg ? (
            <OrgIcon icon={activeOrg.icon} displayName={activeOrg.displayName} />
          ) : (
            <Building2 className="h-3.5 w-3.5" aria-hidden />
          )}
          <span className="truncate text-[13px] font-medium text-foreground">
            {activeOrg?.displayName ?? 'Pick Workspace'}
          </span>
          <ChevronsUpDown
            className="h-3 w-3 shrink-0 text-muted-foreground transition-opacity opacity-60 group-hover:opacity-100"
            aria-hidden
          />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-72">
        <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-muted-foreground">
          Workspaces
        </DropdownMenuLabel>
        <div className="max-h-72 overflow-y-auto">
          {orgs.map((org) => {
            const isActive = org.id === activeOrgId;
            return (
              <DropdownMenuItem
                key={org.id}
                onSelect={() => setActiveOrg(org.id)}
                className={cn(
                  'flex items-center gap-2',
                  isActive && 'bg-foreground/[0.04] text-foreground',
                )}
              >
                <OrgIcon icon={org.icon} displayName={org.displayName} />
                <div className="flex min-w-0 flex-1 flex-col leading-tight">
                  <span className="truncate text-[13px] font-medium">{org.displayName}</span>
                  <span className="truncate text-[11px] text-muted-foreground">{org.slug}</span>
                </div>
                {isActive && <Check className="h-3.5 w-3.5 shrink-0" aria-hidden />}
              </DropdownMenuItem>
            );
          })}
        </div>
        {onCreate && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={onCreate}>
              <Plus className="h-3.5 w-3.5" aria-hidden />
              <span>New Workspace</span>
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
